import { MouseEvent as ReactMouseEvent, ReactNode, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { FontSizes, FontWeights, Text, TextButton } from '@calm-web/design-system';

import Image from '@/components/image/NextImage';
import Branding from '@/components/SimplifiedSignup/Components/Branding';
import ValuePropsList from '@/components/valuePropList';
import { useAnalytics } from '@/hooks/analytics';
import { useDeviceState, useUserState } from '@/hooks/store';
import { useIsMobile } from '@/hooks/ui/useIsMobile';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import { calmLogger } from '@/utils/calmLogger';
import AppleStore from 'icons/apple-store.svg';
import DownloadGooglePlay from 'icons/download-google-play.svg';

import { useSimplifiedSignupContext } from '../Context';
import messages from './messages';
import {
	AppDownloadContainer,
	AppDownloadItemContainer,
	AppImageWrapper,
	CancelSubscriptionItemContainer,
	CTAButton,
	CTALink,
	CTAWrapper,
	CustomAppImageWrapper,
	HandoffSubheading,
	HandoffWelcomeContainer,
	IconButton,
	Line,
	QRCode,
	QRCodeWrapper,
	SimplifiedSignupHandoffContainer,
	SocialWrapper,
	TrueMedQualificationHeading,
	TrueMedQualificationLink,
	ValuePropsWrapper,
} from './styles';
import { HandoffScreenConfig } from './types';

const SimplifiedSignupHandoff = () => {
	const user = useUserState();
	const { formatMessage } = useIntl();
	const routerPush = useRouterPush();
	const device = useDeviceState();
	const { logEvent, logEventAsync } = useAnalytics();
	const { name, activeScreenConfig, logPageViewEvent, coBranded, logo } = useSimplifiedSignupContext();
	const {
		CTA,
		upsellBullets = [],
		isWithinModal,
		showBranding = false,
		backgroundKey,
		heading,
		subHeading,
		hideQRCode = {
			mobile: false,
			desktop: false,
		},
		hasTrueMedSupport = false,
		qrCodeImage,
		qrCodeInstructions,
		hideDownloadOnTheAppStore = false,
		hideGetItOnGooglePlay = false,
		appImage,
		appStoreUrl = 'https://calm.onelink.me/Pf5F/oislygnz',
		ignoreSubscriptionStatus = false,
		cancelSubscriptionUrl = 'https://support.calm.com/hc/en-us/articles/115002473607-How-to-Cancel-Your-Subscription-or-Free-Trial',
	} = activeScreenConfig as HandoffScreenConfig;
	const [isMobile] = useIsMobile();
	// A ref for the guard itself and state for the loading look. The ref refuses a second press
	// that lands before React has re-rendered with the pending state.
	const ctaInFlight = useRef(false);
	const [isCtaPending, setIsCtaPending] = useState(false);

	let AppStore = null;
	if (device.isMobile) {
		if (device.os === 'AndroidOS' && !hideGetItOnGooglePlay) {
			AppStore = DownloadGooglePlay;
		} else if (!hideDownloadOnTheAppStore) {
			AppStore = AppleStore;
		}
	}

	useComponentDidMount(() => {
		logPageViewEvent('handoff');
	});

	function logReturnClick(): void {
		if (CTA?.logsOwnClickEvent) return;
		logEvent({
			eventName: `${name} : Return : Clicked`,
			eventProps: {
				source: name,
				platform: device?.os || null,
				referrer: 'Payment',
			},
		});
	}

	function logCtaFailure(error: unknown): void {
		calmLogger.error('Handoff CTA onClick failed', { source: name }, error);
	}

	// The text and link CTAs keep their long-standing fire-and-forget behaviour.
	function _CTA(e?: ReactMouseEvent<HTMLAnchorElement>): void {
		e?.preventDefault?.();
		logReturnClick();
		if (CTA) {
			Promise.resolve(CTA.onClick(routerPush, { device, logEvent, logEventAsync })).catch(logCtaFailure);
		}
	}

	// The primary button is the only CTA that does async work before navigating, so it alone gets
	// the one-press-at-a-time guard and the loading look. A double tap inside that window would
	// log the click twice and, on a phone, queue a second store fallback.
	function onPrimaryPress(): void {
		if (ctaInFlight.current) return;
		ctaInFlight.current = true;
		setIsCtaPending(true);
		logReturnClick();

		const release = (): void => {
			ctaInFlight.current = false;
			setIsCtaPending(false);
		};
		// The Promise constructor turns a synchronous throw into a rejection, so release runs either
		// way. Promise.resolve(f()) would let the throw escape first and leave the button disabled.
		new Promise<void>(resolve => {
			resolve(CTA?.onClick(routerPush, { device, logEvent, logEventAsync }));
		}).then(release, error => {
			release();
			logCtaFailure(error);
		});
	}

	async function goToAppStore() {
		logEvent({
			eventName: `${name} : Install : Clicked`,
			eventProps: {
				source: name,
				platform: device?.os || null,
			},
		});
		await routerPush(appStoreUrl);
	}

	const onTrueMedQualificationClick = () => {
		logEvent({
			eventName: 'TrueMed : Survey Link : Clicked',
		});
	};

	const getTrueMedQualificationLink = () => {
		return `https://app.truemed.com/qualify/${process.env.NEXT_PUBLIC_TRUEMED_PUBLIC_QUALIFICATION_ID}?user_id=${user?.id}`;
	};

	const getTrueMedQualificationCopy = () => {
		if (!user || !hasTrueMedSupport) {
			return null;
		}

		return (
			<TrueMedQualificationHeading>
				{formatMessage(messages.trueMedQualification, {
					qualifyHerelink: (...chunks: ReactNode[]) => (
						<TrueMedQualificationLink
							href={getTrueMedQualificationLink()}
							target="_blank"
							rel="noopener noreferrer"
							onClick={onTrueMedQualificationClick}
						>
							{chunks}
						</TrueMedQualificationLink>
					),
				})}
			</TrueMedQualificationHeading>
		);
	};

	return (
		<SimplifiedSignupHandoffContainer isWithinModal={isWithinModal} isCompact={isWithinModal}>
			{/* An explicit opt-in, not inferred from showHeader. Several flows already turn the site
			    header off on this screen and must keep their header-less layout as it is. */}
			{showBranding && <Branding backgroundColor={backgroundKey} coBranded={coBranded} logo={logo} />}
			<HandoffWelcomeContainer>
				<Text
					data-testid="handoff-heading"
					role="alert"
					aria-live="polite"
					el="h1"
					size={FontSizes['2xl']}
					weight={FontWeights.Medium}
				>
					{formatMessage(heading ? heading() : messages.handoffHeader)}
				</Text>
				<HandoffSubheading
					data-testid="handoff-subheading"
					align="center"
					el="p"
					size={FontSizes.lg}
					weight={FontWeights.Regular}
				>
					{formatMessage(subHeading ?? messages.subHeading)}
				</HandoffSubheading>
				{getTrueMedQualificationCopy()}
				{AppStore && (
					<IconButton
						backgroundColor="transparent"
						aria-label={formatMessage(messages.toAppStore)}
						onPress={goToAppStore}
					>
						<AppStore />
					</IconButton>
				)}
			</HandoffWelcomeContainer>
			{ignoreSubscriptionStatus && (
				<CancelSubscriptionItemContainer>
					<Text align="center" el="h3" size={FontSizes.lg} weight={FontWeights.Medium}>
						{formatMessage(messages.cancelActiveSubscriptionHeading)}
					</Text>
					<Text align="center" size={FontSizes.base} weight={FontWeights.Light}>
						{formatMessage(messages.cancelActiveSubscription, {
							link: (...chunks: ReactNode[]) => (
								<a href={cancelSubscriptionUrl} target="_blank" rel="noopener noreferrer">
									{chunks}
								</a>
							),
						})}
					</Text>
				</CancelSubscriptionItemContainer>
			)}
			<AppDownloadContainer>
				<AppDownloadItemContainer isHidden={isMobile ? hideQRCode.mobile : hideQRCode.desktop}>
					<QRCodeWrapper>
						<Text
							data-testid="handoff-qr-code-instructions"
							align="center"
							el="h2"
							size={FontSizes.lg}
							weight={FontWeights.Medium}
						>
							{formatMessage(qrCodeInstructions ?? messages.qrCodeInstructions)}
						</Text>
						<QRCode>
							<Image
								data-testid="handoff-qr-code-image"
								{...(qrCodeImage ?? {
									src: '/_n/images/onboarding/handoff_skipped_qr.png',
									alt: 'Scan QR code to download Calm app',
									height: 148,
									width: 148,
								})}
							/>
						</QRCode>
						<SocialWrapper>
							{!hideDownloadOnTheAppStore && <AppleStore data-testid="handoff-download-on-the-app-store" />}
							{!hideGetItOnGooglePlay && <DownloadGooglePlay data-testid="handoff-get-it-on-google-play" />}
						</SocialWrapper>
					</QRCodeWrapper>
				</AppDownloadItemContainer>
				<AppDownloadItemContainer>
					{appImage ? (
						<CustomAppImageWrapper>
							<Image data-testid="handoff-app-image" fill style={{ objectFit: 'contain' }} {...appImage} />
						</CustomAppImageWrapper>
					) : (
						<AppImageWrapper>
							<Image
								data-testid="handoff-app-image"
								fill
								src="/_n/images/dtc-partners/spotify-handoff-device.png"
								alt=""
							/>
						</AppImageWrapper>
					)}
				</AppDownloadItemContainer>
			</AppDownloadContainer>
			{upsellBullets.length > 0 && (
				<ValuePropsWrapper>
					<Text el="h3" size={FontSizes.lg} weight={FontWeights.Medium}>
						{formatMessage(messages.valuePropsHeader, {
							u: (...chunks: ReactNode[]) => <u>{chunks}</u>,
						})}
					</Text>
					<ValuePropsList copy={upsellBullets} buttonColor="blue3" />
					<Line />
				</ValuePropsWrapper>
			)}
			{CTA && (
				<CTAWrapper isWithinModal={isWithinModal}>
					{CTA.description && (
						<Text el="p" size={FontSizes.base}>
							{formatMessage(CTA.description)}
						</Text>
					)}
					{CTA.primary ? (
						<CTAButton
							data-testid="handoff-primary-cta"
							backgroundColor="buttonPrimaryBg"
							isLoading={isCtaPending}
							onPress={onPrimaryPress}
						>
							{formatMessage(CTA.label)}
						</CTAButton>
					) : CTA.href ? (
						<CTALink href={CTA.href} onClick={_CTA}>
							{formatMessage(CTA.label)}
						</CTALink>
					) : (
						<TextButton onClick={_CTA}>{formatMessage(CTA.label)}</TextButton>
					)}
				</CTAWrapper>
			)}
		</SimplifiedSignupHandoffContainer>
	);
};

export default SimplifiedSignupHandoff;
